import {
  AbstractFilterWidget,
  AbstractFilterWidgetOperatorTrigger,
  AbstractFilterWidgetValueTrigger,
  FilterWidgetPropsBase
} from './_private/AbstractFilterWidget';
import { forwardRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from 'v5/design-system/Select';
import { useTranslation } from 'react-i18next';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button } from 'v5/design-system/Button';
import {
  InputGroup,
  InputGroupInput,
  InputGroupSlot
} from 'v5/design-system/InputGroup';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { useForm } from 'react-hook-form';

export type NumberFilterOperator =
  | 'equal'
  | 'notEqual'
  | 'greaterThan'
  | 'lessThan'
  | 'between';

export type NumberFilterValue =
  | {
      operator: 'equal' | 'notEqual' | 'greaterThan' | 'lessThan';
      value: number | null;
    }
  | { operator: 'between'; value: { from: number | null; to: number | null } };

export interface NumberFilterWidgetProps
  extends FilterWidgetPropsBase<NumberFilterValue> {
  step?: number;
  min?: number;
  max?: number;
  /**
   * Optional placeholder for the numeric input(s)
   */
  placeholder?: string;
  /**
   * Optional prefix displayed before numbers (e.g., "$"). Can be a ReactNode.
   */
  valuePrefix?: ReactNode;
  /**
   * Optional postfix displayed after numbers (e.g., "€", "kg"). Can be a ReactNode.
   */
  valuePostfix?: ReactNode;
  filterField?: string;
}

function isRange(
  v: NumberFilterValue
): v is Extract<NumberFilterValue, { operator: 'between' }> {
  return v.operator === 'between';
}

const NumberFilterWidgetInner = (
  {
    filterName,
    filterIcon,
    onRemove,
    value,
    onValueChange,
    step = 1,
    min,
    max,
    placeholder,
    valuePrefix,
    valuePostfix,
    filterField,
    ...rest
  }: NumberFilterWidgetProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const { t } = useTranslation('patterns');

  const form = useForm<{
    single: string;
    from: string;
    to: string;
  }>({
    defaultValues: {
      single: '',
      from: '',
      to: ''
    }
  });

  const renderValueNode = useMemo(() => {
    if (isRange(value)) {
      const from = value.value.from;
      const to = value.value.to;
      return (
        <span className="inline-flex items-center gap-1">
          {valuePrefix}
          <span>{from ?? 0}</span>
          {valuePostfix}
          <span className="text-fg-muted">{t('NumberFilterWidget.to')}</span>
          {valuePrefix}
          <span>{to ?? 0}</span>
          {valuePostfix}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1">
        {valuePrefix}
        <span>{value.value ?? 0}</span>
        {valuePostfix}
      </span>
    );
  }, [value, t, valuePrefix, valuePostfix]);

  const [open, setOpen] = useState(false);
  const values = form.watch();

  useEffect(() => {
    if (!open) return;
    if (isRange(value)) {
      form.reset({
        from: value.value.from == null ? '' : String(value.value.from),
        to: value.value.to == null ? '' : String(value.value.to),
        single: ''
      });
    } else {
      form.reset({
        single: value.value == null ? '' : String(value.value),
        from: '',
        to: ''
      });
    }
  }, [open, value, form]);

  const parseNum = (s: string): number | null => {
    if (s.trim() === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const canApply = useMemo(() => {
    if (value.operator === 'between') {
      return parseNum(values.from) !== null && parseNum(values.to) !== null;
    }
    return parseNum(values.single) !== null;
  }, [value.operator, values.from, values.to, values.single]);

  const submitForm = form.handleSubmit(formValues => {
    if (value.operator === 'between') {
      const from = parseNum(formValues.from);
      const to = parseNum(formValues.to);
      if (from != null && to != null) {
        onValueChange({
          operator: 'between',
          value: { from, to }
        });
        setOpen(false);
      }
    } else {
      const n = parseNum(formValues.single);
      if (n != null) {
        onValueChange({
          operator: value.operator,
          value: n
        } as NumberFilterValue);
        setOpen(false);
      }
    }
  });

  const handleInputKeyDown: React.KeyboardEventHandler<
    HTMLInputElement
  > = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      submitForm();
    }
  };

  return (
    <AbstractFilterWidget
      ref={ref}
      filterName={filterName}
      filterIcon={filterIcon}
      onRemove={onRemove}
      filterField={filterField}
      {...rest}
      operatorSlot={
        <Select
          value={value.operator}
          onValueChange={next => {
            if (next === 'between') {
              onValueChange({
                operator: 'between',
                value: { from: null, to: null }
              });
            } else {
              onValueChange({
                operator: next as Exclude<NumberFilterOperator, 'between'>,
                value: null
              } as NumberFilterValue);
            }
          }}
        >
          <SelectTrigger asChild>
            <AbstractFilterWidgetOperatorTrigger>
              {value.operator === 'equal'
                ? t('NumberFilterWidget.operator.equal')
                : value.operator === 'notEqual'
                  ? t('NumberFilterWidget.operator.notEqual')
                  : value.operator === 'greaterThan'
                    ? t('NumberFilterWidget.operator.greaterThan')
                    : value.operator === 'lessThan'
                      ? t('NumberFilterWidget.operator.lessThan')
                      : t('NumberFilterWidget.operator.between')}
            </AbstractFilterWidgetOperatorTrigger>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="equal">
              {t('NumberFilterWidget.operator.equal')}
            </SelectItem>
            <SelectItem value="notEqual">
              {t('NumberFilterWidget.operator.notEqual')}
            </SelectItem>
            <SelectItem value="greaterThan">
              {t('NumberFilterWidget.operator.greaterThan')}
            </SelectItem>
            <SelectItem value="lessThan">
              {t('NumberFilterWidget.operator.lessThan')}
            </SelectItem>
            <SelectItem value="between">
              {t('NumberFilterWidget.operator.between')}
            </SelectItem>
          </SelectContent>
        </Select>
      }
      valueSlot={
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <AbstractFilterWidgetValueTrigger data-element="value-trigger">
              {renderValueNode}
            </AbstractFilterWidgetValueTrigger>
          </PopoverTrigger>
          <PopoverContent
            className="p-2 w-fit"
            align="start"
            onInteractOutside={() => {
              void submitForm();
            }}
          >
            <form className="contents" onSubmit={submitForm}>
              {value.operator === 'between' ? (
                <div className="flex items-center gap-2">
                  <InputGroup
                    variant="regular"
                    size="sm"
                    className="w-28"
                    leftSlot={
                      valuePrefix ? (
                        <InputGroupSlot className="text-fg-muted text-sm">
                          {valuePrefix}
                        </InputGroupSlot>
                      ) : undefined
                    }
                    rightSlot={
                      valuePostfix ? (
                        <InputGroupSlot className="text-fg-muted text-sm">
                          {valuePostfix}
                        </InputGroupSlot>
                      ) : undefined
                    }
                  >
                    <InputGroupInput
                      type="number"
                      inputMode="decimal"
                      step={step}
                      min={min}
                      max={max}
                      {...form.register('from')}
                      placeholder={placeholder ?? '0'}
                    />
                  </InputGroup>
                  <span className="text-fg-muted text-sm">
                    {t('NumberFilterWidget.to')}
                  </span>
                  <InputGroup
                    variant="regular"
                    size="sm"
                    className="w-28"
                    leftSlot={
                      valuePrefix ? (
                        <InputGroupSlot className="text-fg-muted text-sm">
                          {valuePrefix}
                        </InputGroupSlot>
                      ) : undefined
                    }
                    rightSlot={
                      valuePostfix ? (
                        <InputGroupSlot className="text-fg-muted text-sm">
                          {valuePostfix}
                        </InputGroupSlot>
                      ) : undefined
                    }
                  >
                    <InputGroupInput
                      type="number"
                      inputMode="decimal"
                      step={step}
                      min={min}
                      max={max}
                      {...form.register('to')}
                      placeholder={placeholder ?? '0'}
                    />
                  </InputGroup>
                </div>
              ) : (
                <InputGroup
                  variant="regular"
                  size="sm"
                  className="w-40"
                  leftSlot={
                    valuePrefix ? (
                      <InputGroupSlot className="text-fg-muted text-sm">
                        {valuePrefix}
                      </InputGroupSlot>
                    ) : undefined
                  }
                  rightSlot={
                    valuePostfix ? (
                      <InputGroupSlot className="text-fg-muted text-sm">
                        {valuePostfix}
                      </InputGroupSlot>
                    ) : undefined
                  }
                >
                  <InputGroupInput
                    type="number"
                    inputMode="decimal"
                    step={step}
                    min={min}
                    max={max}
                    {...form.register('single')}
                    placeholder={placeholder ?? '0'}
                  />
                </InputGroup>
              )}
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => setOpen(false)}
                >
                  {t('NumberFilterWidget.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={!canApply}
                >
                  {t('NumberFilterWidget.apply')}
                </Button>
              </div>
            </form>
          </PopoverContent>
        </Popover>
      }
    />
  );
};

export const NumberFilterWidget = forwardRef(NumberFilterWidgetInner) as (
  props: NumberFilterWidgetProps & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof NumberFilterWidgetInner>;
